import { useEffect, useRef, useState } from 'react';
import type { ViewKey } from '@dash/shared';

export interface RotatorState {
  current: ViewKey;
  next: ViewKey;
  /** 0..1 — fracao de tempo decorrido ate a proxima troca */
  progress: number;
}

/**
 * Rotaciona entre views a cada N segundos.
 * - intervalSec=0 desativa (current = initial, next = current, progress = 0).
 * - Trocar `initial` (ex: keyboard 1/2/3) reinicia o ciclo no novo ponto.
 * - Tick interno em 250ms para barra de progresso suave sem custo.
 */
export function useRotator(views: ViewKey[], intervalSec: number, initial: ViewKey): RotatorState {
  const [current, setCurrent] = useState<ViewKey>(initial);
  const [progress, setProgress] = useState(0);
  const startRef = useRef<number>(Date.now());

  // reseta ciclo quando view manual muda
  useEffect(() => {
    setCurrent(initial);
    startRef.current = Date.now();
    setProgress(0);
  }, [initial]);

  useEffect(() => {
    if (!intervalSec || views.length < 2) {
      setProgress(0);
      return;
    }
    const total = intervalSec * 1000;
    startRef.current = Date.now();
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      if (elapsed >= total) {
        startRef.current = Date.now();
        setCurrent((cur) => {
          const idx = views.indexOf(cur);
          return views[(idx + 1) % views.length] ?? cur;
        });
        setProgress(0);
      } else {
        setProgress(elapsed / total);
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [views, intervalSec]);

  const idx = views.indexOf(current);
  const next = views[(idx + 1) % views.length] ?? current;

  return { current, next, progress };
}
